import React from 'react';
import { 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  IconButton
} from '@mui/material';
import { 
  FavoriteBorder, 
  ShoppingCartOutlined,
  Visibility 
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Product } from '../types';

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  cursor: 'pointer',
  borderRadius: 8,
  border: '1px solid transparent',
  transition: 'all 0.2s ease-in-out',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
  
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    border: '1px solid #EAEDEF',
    
    '& .quick-actions': {
      opacity: 1,
      transform: 'translateY(0)',
    },
    
    '& .product-image': {
      transform: 'scale(1.05)',
    },
  },
}));

const ImageWrapper = styled(Box)({
  position: 'relative',
  paddingTop: '100%',
  overflow: 'hidden',
  backgroundColor: '#F5F7FA',
});

const StyledCardMedia = styled(CardMedia)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease-in-out',
}) as typeof CardMedia;

const QuickActions = styled(Box)({
  position: 'absolute',
  bottom: 12,
  right: 12,
  display: 'flex',
  gap: 8,
  opacity: 0,
  transform: 'translateY(10px)',
  transition: 'all 0.2s ease-in-out',
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: '1px solid #EAEDEF',
  
  '&:hover': {
    backgroundColor: '#35C5F0',
    borderColor: '#35C5F0',
    color: '#FFFFFF',
  },
}));

const BadgeContainer = styled(Box)({
  position: 'absolute',
  top: 12,
  left: 12,
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
  zIndex: 1,
});

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onAddToWishlist?: (product: Product) => void;
  onQuickView?: (product: Product) => void;
  onClick?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onAddToCart,
  onAddToWishlist,
  onQuickView,
  onClick
}) => {
  const discountPercentage = product.comparePrice 
    ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
    : 0;

  const isSale = product.comparePrice && product.comparePrice > product.price;
  const isNew = product.createdAt && new Date(product.createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30일 이내
  const isSoldOut = product.stockQuantity === 0;

  const handleCardClick = () => {
    if (onClick) {
      onClick(product);
    }
  };

  return (
    <StyledCard onClick={handleCardClick}>
      <ImageWrapper>
        <StyledCardMedia
          component="img"
          image={product.imageUrl || 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400'}
          alt={product.name}
          className="product-image"
        />
        
        <BadgeContainer>
          {isSale && discountPercentage > 0 && (
            <Chip
              label={`${discountPercentage}%`}
              size="small"
              sx={{
                backgroundColor: '#FF6B6B',
                color: '#FFFFFF',
                fontWeight: 700,
                height: 24,
                fontSize: 11,
              }}
            />
          )}
          {isNew && !isSale && (
            <Chip
              label="NEW"
              size="small"
              sx={{
                backgroundColor: '#35C5F0',
                color: '#FFFFFF',
                fontWeight: 700,
                height: 24,
                fontSize: 11,
              }}
            />
          )}
          {product.featured && !isSale && !isNew && (
            <Chip
              label="HOT"
              size="small"
              sx={{
                backgroundColor: '#FFB800',
                color: '#FFFFFF',
                fontWeight: 700,
                height: 24,
                fontSize: 11,
              }}
            />
          )}
          {isSoldOut && (
            <Chip
              label="SOLD OUT"
              size="small"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: '#FFFFFF',
                fontWeight: 700,
                height: 24,
                fontSize: 11,
              }}
            />
          )}
        </BadgeContainer>
        
        <QuickActions className="quick-actions">
          {onQuickView && (
            <ActionButton 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuickView(product);
              }}
              aria-label="Quick view"
            >
              <Visibility fontSize="small" />
            </ActionButton>
          )}
          {onAddToWishlist && (
            <ActionButton 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddToWishlist(product);
              }}
              aria-label="Add to wishlist"
            >
              <FavoriteBorder fontSize="small" />
            </ActionButton>
          )}
          {!isSoldOut && (
            <ActionButton 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(product);
              }}
              aria-label="Add to cart"
            >
              <ShoppingCartOutlined fontSize="small" />
            </ActionButton>
          )}
        </QuickActions>
      </ImageWrapper>
      
      <CardContent sx={{ p: 2 }}>
        {product.brand && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#9CA3AF', 
              fontWeight: 500,
              display: 'block',
              mb: 0.5,
            }}
          >
            {product.brand}
          </Typography>
        )}
        
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 400,
            color: '#1F2937',
            lineHeight: 1.4,
            mb: 1,
            minHeight: 40,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {product.name}
        </Typography>
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#6B7280',
            display: 'block',
            mb: 1.5,
          }}
        >
          {product.categoryName}
        </Typography>
        
        <Box>
          {product.comparePrice && product.comparePrice > product.price && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 700,
                  color: '#FF6B6B',
                }}
              >
                {discountPercentage}%
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#9CA3AF',
                  textDecoration: 'line-through',
                }}
              >
                ₱{product.comparePrice.toLocaleString()}
              </Typography>
            </Box>
          )}
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: isSoldOut ? '#9CA3AF' : '#1F2937',
              fontSize: '18px',
            }}
          >
            ₱{product.price.toLocaleString()}
          </Typography>
          
          {isSoldOut && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#FF5757',
                fontWeight: 600,
                mt: 0.5,
                display: 'block',
              }}
            >
              Out of stock
            </Typography>
          )}
          
          {!isSoldOut && product.stockQuantity <= 5 && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#FFB800',
                fontWeight: 600,
                mt: 0.5,
                display: 'block',
              }}
            >
              Only {product.stockQuantity} left
            </Typography>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default ProductCard;